/**
 * The fab quote (M8 plan MC-c2, 5(c)): one pure function prices a fab order at a docked station. The credit share
 * of the module's price, plus a source plan per input: what the hold covers, then the station's berth, then what
 * the station sells ("buy missing"), and what is left `short`.
 *
 * - **Buy missing** is priced by `market.quote(…, 'buy')`: the strategic `quoteRow` total under the station's tariff
 *   (`strategic-link/market.ts`). Its `buyUnit` already carries the M4 buy markup, so no markup is added here. It
 *   covers only what the station sells and has in stock (a stock-clamped quote buys what is there; the rest is
 *   short), and only where `marketRefusal` (D-388, the M7 retaliation refusals) allows the goods buy. Without a
 *   strategic link (`market === null`) nothing can be bought.
 * - **The carry** (plan decision 3) is never bought: it comes from the hold or the berth, or it is short. A component
 *   carry (a data core) counts whole free components: never sealed, story or contract cargo.
 * - No labour fee. The fabricated module has the module's full stats ("cheaper, not better"); the quote attaches them.
 * - **A salvaged component** (MC-c8, the recipe's `salvageAlt`) of the named type at `minBand` or better
 *   (`salvage/generate.ts conditionBand`; Wrecked never) may stand in for the item it `replaces` (an input or the
 *   carry). Story, sealed, contract and contraband components never do (`career/berth.ts berthRefusal`: a given
 *   component is parked in the order station's berth). `salvage.candidates` lists the ones in the hold and this
 *   station's berth; quoting with one of them (`salvage`) leaves the replaced item out. A component an open order
 *   already holds (`FabOrder.salvageIn`) counts for nothing here, as a carry or a candidate.
 * - **Remote berths** (MC-b7, MC-c5b): with `remote` (`craft/fabOrder.ts remoteBerths` for this station: remote pull
 *   unlocked, the player's other open berths within reach, nearest first), an input or a ware/good carry then draws
 *   those berths in that order, as `deliverFab` does, before anything is bought. A component carry never does (D-490).
 *
 * MC-c3a/c3b's fab orders, MC-c4's shortage card, MC-c5's Outfitting rows and MC-c8's salvaged inputs read it.
 */
import { berthRefusal, type BerthRefusal } from '../career/berth'
import { EPSILON } from '../career/ops'
import { goodT, isGoodId } from '../career/parts'
import type { CommandError } from '../career/result'
import type { CargoComponent, Career, FabCarryId, GoodOrWare } from '../career/types'
import { resolveModule } from '../data/m2'
import type { ComponentType, FabMinBand, M2Data, ModuleDef, ModuleId, StationDef, StationId } from '../data/m2Schemas'
import { CONDITION_BAND, conditionBand, type ConditionBand } from '../salvage/generate'
import { marketRefusal } from '../story/refusals'
import type { RemoteBerth } from './fabOrder'
import type { CraftMarket } from './hook'
import { isComponentType, recipeFor, type FabRecipe } from './recipes'

/** One remote berth's share of an item (MC-b7 remote pull): what `deliverFab` would draw from it. */
export interface FabRemoteDraw {
  stationId: StationId
  /** Relay jumps from the order's station (0: the same system). */
  jumps: number
  qty: number
}

/** Where one input's tonnes come from (hold → berth → remote berths → buy missing → short; they sum to `need`). */
export interface FabInputPlan {
  /** Tonnes the recipe needs. */
  need: number
  fromHold: number
  fromBerth: number
  /** Tonnes the player's other berths in reach cover (remote pull, MC-b7); 0 without `remote`. */
  fromRemote: number
  /** `fromRemote` by berth, nearest first (empty when none). */
  remote: FabRemoteDraw[]
  /** Tonnes the station sells now (stock-clamped). */
  buyMissing: number
  /** Credits for `buyMissing` (the rounded quote total under tariff, as the wallet books it); 0 when none. */
  buyCredits: number
  short: number
}

/**
 * The carry's plan: never bought, so hold → berth → remote berths → short. Tonnes for a ware or good, whole units for
 * a component.
 */
export interface FabCarryPlan {
  id: FabCarryId
  need: number
  fromHold: number
  fromBerth: number
  /** Remote berths in reach (a ware or good carry only: a component carry is never pulled, D-490). */
  fromRemote: number
  remote: FabRemoteDraw[]
  short: number
}

/** The recipe's salvaged-component alternative (MC-c8) as it stands for this Career at this station. */
export interface FabSalvagePlan {
  /** The component type that may stand in. */
  component: ComponentType
  /** The input or the carry it stands in for. */
  replaces: FabCarryId
  /** The worst condition band it may be in. */
  minBand: FabMinBand
  /** Ids of the components that qualify (`salvageRefusal` null, held by no open order): the hold's, then the berth's. */
  candidates: string[]
  /** The candidate standing in for `replaces` in this quote (the `salvage` argument when it qualifies), else null. */
  use: string | null
}

export interface FabQuote {
  moduleId: ModuleId
  /** The module's full credit price (the ready-made price). */
  price: number
  /** Credits charged at start (the credit share, rounded). */
  credits: number
  /** Credits for every input's `buyMissing`, under the station's tariff. */
  buyCredits: number
  /** One plan per input; an input a salvaged component stands in for (`salvage.use`) is left out. */
  inputs: Partial<Record<GoodOrWare, FabInputPlan>>
  /** The carry is never bought. When a salvaged component stands in for it, its `need` is 0. */
  carry: FabCarryPlan
  /** The recipe's salvaged-component alternative (MC-c8), or null when it has none. */
  salvage: FabSalvagePlan | null
  /** The fabricated module's stats: the full stats of the module, as bought ready-made. */
  stats: ModuleDef['stats']
  /** Why this station sells the player nothing now (M7 retaliation, D-388), or null. */
  refusal: CommandError | null
  /** True when nothing is short: every input and the carry can be sourced here now. */
  complete: boolean
}

/** Why a component can't stand in for a recipe's `salvageAlt` (MC-c8): the wrong type, too worn, or held back. */
export type SalvageRefusal = 'wrongComponent' | 'band' | BerthRefusal

/** The worst `CONDITION_BAND` each `minBand` accepts (Intact 0 < Serviceable 1 < Wrecked 2). */
const WORST_BAND: Readonly<Record<FabMinBand, ConditionBand>> = { intact: CONDITION_BAND.intact, serviceable: CONDITION_BAND.serviceable }

/**
 * Whether salvaged component `c` may stand in for `alt` (MC-c8): the named type, at `minBand` or better
 * (`conditionBand` over `salvage.json bands`; Wrecked never qualifies), and not contract, story, sealed or contraband
 * cargo (`berthRefusal`: a given component waits in the order station's berth). Null when it may. Pure.
 */
export function salvageRefusal(
  c: Readonly<CargoComponent>,
  alt: NonNullable<FabRecipe['salvageAlt']>,
  bands: M2Data['salvage']['bands'],
): SalvageRefusal | null {
  if (c.type !== alt.component) return 'wrongComponent'
  const held = berthRefusal(c)
  if (held !== null) return held
  return conditionBand(c.condition, bands) > WORST_BAND[alt.minBand] ? 'band' : null
}

/** Ids of the salvaged components open fab orders hold (`FabOrder.salvageIn`): no carry, candidate or order may use them. */
export function heldSalvage(career: Career): ReadonlySet<string> {
  const out = new Set<string>()
  for (const o of career.fabOrders ?? []) for (const id of o.salvageIn) out.add(id)
  return out
}

/** A hold or berth component that may serve as a carry: the right type, and not sealed, story or contract cargo. */
function freeComponent(c: Readonly<CargoComponent>, type: string, held: ReadonlySet<string>): boolean {
  return c.type === type && c.sealed !== true && c.story !== true && c.contractId === undefined && !held.has(c.id)
}

function countFree(components: readonly CargoComponent[], type: string, held: ReadonlySet<string>): number {
  let n = 0
  for (const c of components) if (freeComponent(c, type, held)) n++
  return n
}

/** Tonnes (or units) of `id` in the hold. */
function inHold(career: Career, id: FabCarryId, held: ReadonlySet<string>): number {
  if (isComponentType(id)) return countFree(career.cargo.components, id, held)
  if (isGoodId(id)) return goodT(career.cargo.parts, id)
  return career.cargo.wares[id]
}

/** Tonnes (or units) of `id` in the player's berth at `stationId`. */
function inBerth(career: Career, stationId: string, id: FabCarryId, held: ReadonlySet<string>): number {
  const berth = career.storage?.[stationId]
  if (berth === undefined) return 0
  if (isComponentType(id)) return countFree(berth.components, id, held)
  if (isGoodId(id)) return goodT(berth.goods, id)
  return berth.wares[id] ?? 0
}

function positive(x: number): number {
  return x > 0 ? x : 0
}

/** `x` when it is more than float dust (`EPSILON`), else 0: what is still short after the draws. */
function settled(x: number): number {
  return x > EPSILON ? x : 0
}

/** Draws up to `want` of ware or good `id` from `berths` in order (`deliverFab`'s remote rule, MC-b7); the draws taken. */
export function remoteDraw(career: Career, berths: readonly RemoteBerth[], id: GoodOrWare, want: number, held: ReadonlySet<string>): FabRemoteDraw[] {
  const out: FabRemoteDraw[] = []
  let took = 0
  for (const b of berths) {
    if (!(want - took > EPSILON)) break
    const qty = Math.min(want - took, positive(inBerth(career, b.stationId, id, held)))
    if (!(qty > EPSILON)) continue
    out.push({ stationId: b.stationId, jumps: b.jumps, qty })
    took += qty
  }
  return out
}

function drawn(draws: readonly FabRemoteDraw[]): number {
  let n = 0
  for (const d of draws) n += d.qty
  return n
}

/** The station's refusal of the goods buy: the ware row or the component row (the runtime's `goodsRefusal`). */
function buyRefusal(career: Career, station: StationDef): CommandError | null {
  const r = marketRefusal(career, station, 'buyWare') ?? marketRefusal(career, station, 'buyComponent')
  return r === null ? null : r.error
}

/** The salvage plan for `recipe` at `stationId` (null without a `salvageAlt`), using `salvage` when it qualifies. */
function salvagePlan(career: Career, data: M2Data, recipe: FabRecipe, stationId: string, held: ReadonlySet<string>, salvage: string | undefined): FabSalvagePlan | null {
  const alt = recipe.salvageAlt
  if (alt === undefined) return null
  const candidates: string[] = []
  const berth = career.storage?.[stationId]?.components ?? []
  for (const c of [...career.cargo.components, ...berth]) {
    if (!held.has(c.id) && salvageRefusal(c, alt, data.salvage.bands) === null) candidates.push(c.id)
  }
  const use = salvage !== undefined && candidates.includes(salvage) ? salvage : null
  return { component: alt.component, replaces: alt.replaces, minBand: alt.minBand, candidates, use }
}

/**
 * Prices fabricating `moduleId` at `station` for `career`, with `market` the strategic market (null without a
 * strategic link: nothing is bought). With `salvage` (a component id, MC-c8), a qualifying salvaged component stands
 * in for the recipe's `salvageAlt.replaces`: that input is left out, or the carry's `need` is 0. Null when the module
 * has no recipe (Mk I, or a Mk II/III without materials). With `remote` (MC-c5b: `remoteBerths(career, station.id,
 * pull)`, empty when remote pull is locked), the player's other berths in reach cover what the hold and this berth
 * don't, nearest first, before anything is bought. Pure: reads the Career, the data and the market's cached quotes;
 * changes nothing.
 */
export function quoteFab(
  career: Career,
  data: M2Data,
  station: StationDef,
  moduleId: ModuleId,
  market: CraftMarket | null,
  salvage?: string,
  remote: readonly RemoteBerth[] = [],
): FabQuote | null {
  const recipe = recipeFor(data, moduleId)
  if (recipe === null) return null
  const def = resolveModule(data, moduleId)
  const held = heldSalvage(career)
  const alt = salvagePlan(career, data, recipe, station.id, held, salvage)
  const replaced = alt !== null && alt.use !== null ? alt.replaces : null
  const refusal = buyRefusal(career, station)
  const inputs: Partial<Record<GoodOrWare, FabInputPlan>> = {}
  let buyCredits = 0
  let complete = true
  for (const [id, need] of Object.entries(recipe.inputs) as [GoodOrWare, number][]) {
    if (id === replaced) continue
    const fromHold = Math.min(need, positive(inHold(career, id, held)))
    const fromBerth = Math.min(need - fromHold, positive(inBerth(career, station.id, id, held)))
    const pulled = remoteDraw(career, remote, id, need - fromHold - fromBerth, held)
    const fromRemote = drawn(pulled)
    const missing = need - fromHold - fromBerth - fromRemote
    let buyMissing = 0
    let credits = 0
    if (missing > EPSILON && market !== null && refusal === null) {
      const q = market.quote(station.id, id, missing, 'buy')
      if (q !== null && q.qty > 0) {
        buyMissing = Math.min(q.qty, missing)
        credits = Math.round(q.total)
      }
    }
    const short = settled(missing - buyMissing)
    if (short > 0) complete = false
    buyCredits += credits
    inputs[id] = { need, fromHold, fromBerth, fromRemote, remote: pulled, buyMissing, buyCredits: credits, short }
  }
  const carryId = recipe.carry
  const carryNeed = carryId === replaced ? 0 : recipe.carryQty
  const carryHold = Math.min(carryNeed, positive(inHold(career, carryId, held)))
  const carryBerth = Math.min(carryNeed - carryHold, positive(inBerth(career, station.id, carryId, held)))
  const carryPulled = isComponentType(carryId) ? [] : remoteDraw(career, remote, carryId, carryNeed - carryHold - carryBerth, held)
  const carryRemote = drawn(carryPulled)
  const carryShort = settled(carryNeed - carryHold - carryBerth - carryRemote)
  if (carryShort > 0) complete = false
  return {
    moduleId,
    price: def.price,
    credits: Math.round(recipe.creditShare * def.price),
    buyCredits,
    inputs,
    carry: { id: carryId, need: carryNeed, fromHold: carryHold, fromBerth: carryBerth, fromRemote: carryRemote, remote: carryPulled, short: carryShort },
    salvage: alt,
    stats: def.stats,
    refusal,
    complete,
  }
}
